import * as React from "react";
import { useRouter } from "next/router";
import {
  IChangeSimEsimOrchestrator,
  IMobileOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { ISimCard } from "@nju/data-access-portal-product";
import { IEsimQRCode } from "@nju/data-access-portal-sim-card";
import { Panel, Separator, Text } from "@nju/ui";
import { Buttons } from "./Buttons";
import { ChangeSimEsimBannerDetails } from "./ChangeSimEsimBannerDetails";
import { getChangeSimEsimBannerCondition } from "./getChangeSimEsimBannerConditions";
import { getChangeSimEsimBannerData } from "./getChangeSimEsimBannerData";

interface IChangeSimEsimBannerProps {
  simId: string | undefined;
  userId: string | undefined;
  subState: IEsimQRCode["subState"] | undefined;
  simNumber: string | undefined;
  deleteOrchestrator?: () => void;
  mobileOrchestrator: IMobileOrchestrator | undefined;
  changeSimEsimOrchestrator?: IChangeSimEsimOrchestrator;
  simCardProduct: ISimCard | undefined;
}

export function ChangeSimEsimBanner({
  simId,
  userId,
  subState,
  simNumber,
  deleteOrchestrator,
  mobileOrchestrator,
  changeSimEsimOrchestrator,
  simCardProduct,
}: IChangeSimEsimBannerProps) {
  const { reload } = useRouter();
  const waybillNumber = mobileOrchestrator?.waybillNumber;

  const bannerState = React.useMemo(() => {
    return getChangeSimEsimBannerCondition({
      simId,
      subState,
      simNumber,
      changeSimEsimOrchestrator,
      simCardProduct,
    });
  }, [simCardProduct, simId, subState, simNumber, changeSimEsimOrchestrator]);

  const panelProps = React.useMemo(() => {
    return getChangeSimEsimBannerData({
      bannerState,
    });
  }, [bannerState]);

  const refresh = React.useCallback(() => {
    reload();
  }, [reload]);

  const handleOpenDPDLink = React.useCallback(() => {
    window.open(`https://tt.dpd.com.pl/${waybillNumber}`, "_blank");
  }, [waybillNumber]);

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const { text, title, iconName, isFoldable } = panelProps;

  return (
    <>
      <Panel
        iconName={iconName}
        isFoldable={isFoldable}
        testId="ftth-order-status-banner"
        title={title}
      >
        {({ isExpanded }) => {
          return isExpanded ? (
            <>
              <Separator size="xs" tone="invisible" />
              <Text
                className="whitespace-pre-line"
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
              >
                {text}
              </Text>
              <Separator size="xs" tone="invisible" />
              <Buttons
                bannerState={bannerState}
                deleteOrchestrator={deleteOrchestrator}
                refreshCallback={refresh}
                userId={userId}
              />
              <ChangeSimEsimBannerDetails
                changeSimEsimOrchestrator={changeSimEsimOrchestrator}
                handleOpenDPDLink={handleOpenDPDLink}
                mobileOrchestrator={mobileOrchestrator}
              />
              <Separator size={{ "@fromTablet": "xxs" }} tone="invisible" />
            </>
          ) : (
            <>
              <Separator size="xs" tone="invisible" />
              <Text
                className="whitespace-pre-line"
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
              >
                {text}
              </Text>
              <Separator size="xs" tone="invisible" />
              <Buttons
                bannerState={bannerState}
                deleteOrchestrator={deleteOrchestrator}
                refreshCallback={refresh}
                userId={userId}
              />
              <Separator size="xxs" tone="invisible" />
            </>
          );
        }}
      </Panel>
      <Separator size="xxs" tone="invisible" />
    </>
  );
}
